import React, {Component} from 'react';
import {inject, observer} from 'mobx-react';
import {action, autorun, computed, observable} from 'mobx';
import {withTooltip} from '../../../components/toolTip/withTooltip';
import {ReactComponent as IconImage} from '../../../assets/images/studio/markup/icon-image.svg';
import {ReactComponent as IconPen} from '../../../assets/images/studio/markup/icon-pen.svg';
import {ReactComponent as IconMusic} from '../../../assets/images/studio/markup/icon-music.svg';
import {ReactComponent as IconHighlighter} from '../../../assets/images/studio/markup/icon-highlighter.svg';
import {ReactComponent as IconShapes} from '../../../assets/images/studio/markup/icon-shapes.svg';
import {ReactComponent as IconStickers} from '../../../assets/images/studio/markup/icon-stickers.svg';
import {ReactComponent as IconText} from '../../../assets/images/studio/markup/icon-text.svg';
import {ReactComponent as IconNotationText} from '../../../assets/images/studio/markup/icon-notation-text.svg';
import {ReactComponent as DragIcon} from '../../../assets/images/studio/markup/markup-drag.svg';
import {
    audio,
    defaultColorMarkupPanel,
    defaultHighlighterColor,
    defaultThickness,
    errorRed,
    instructions,
    notation,
    orange,
    thicknessMarkupPanel,
    video,
    whiteboard,
    yellow
} from '../../../themes/colors';
import './style.scss';
import MarkupMainMenu from './MarkupMainMenu';
import MarkupLeftActions from './MarkupLeftActions';
import MarkupRightActions from './MarkupRightActions';
import {SvgIcon} from '@material-ui/core';
import {MarkUpMenuTypes, ProjectAssetsType} from '../../../constants/enums';
import {withDraggable} from '../../../compose/withDraggable';
import debounce from "lodash/debounce";
import {isMobile} from '../../../utils/screenUtility';

const PANEL_WIDTH = isMobile() ? 368 : 940;
const PANEL_HEIGHT = 128;
const BOTTOM_MARGIN = 24;

@withDraggable({handle: '.drag-bar', savePosition: true, withInitialPos: false})
@withTooltip
@inject('wizardStore', 'mainStore', 'projectStore', 'whiteBoardStore')
@observer
class MarkupPanelContainer extends Component {
    @observable activeColor = defaultColorMarkupPanel;
    @observable activeThickness = defaultThickness;
    @observable isSubMenu = false;
    @observable isStickersMode = false
    @observable showColorPicker = false
    @observable subMenuType = null

    componentDidMount() {
        this.resize();
        window.addEventListener('resize', this.resize);

        this.draglistener = autorun(() => {
            this.props.whiteBoardStore.changeMarkupDragging(this.props.dragged)
        })
    }

    resize = debounce(() => {
        const xDelta = (window.innerWidth - PANEL_WIDTH) / 2
        const yDelta = window.innerHeight;
        const bounds = {
            left: Math.min(-xDelta, 0),
            right: Math.max(xDelta, 0),
            top: Math.min(-yDelta + PANEL_HEIGHT + BOTTOM_MARGIN, 0),
            bottom: Math.max(-BOTTOM_MARGIN, 0)
        }

        this.props.setBounds(bounds);
    }, 250)

    componentWillUnmount() {
        this.modifiedListener && this.modifiedListener();
        this.draglistener && this.draglistener();
        window.removeEventListener("resize", this.resize)
    }

    @computed get buttonsProps() {
        return [
            {
                icon: <IconMusic/>,
                onClick: this.openAnnotationPanel,
                buttonClass: 'insert-annotation',
                isActive: this.props.isAnnotationMode,
                color: video,
                toolTipText: 'markup.tooltip.music.symbols'
            },
            {
                icon: <IconNotationText/>,
                onClick: this.openNotationText,
                buttonClass: 'notation-text',
                isActive: this.isSubMenu,
                color: errorRed,
                toolTipText: 'markup.tooltip.notation.text'
            },
            {
                icon: <IconText/>,
                onClick: this.activateText,
                buttonClass: 'text',
                isActive: this.props.isTextMode,
                color: whiteboard,
                toolTipText: 'markup.tooltip.text'
            },
            {
                icon: <IconPen/>,
                onClick: this.activatePencil,
                buttonClass: 'pen',
                isActive: this.props.isPencilMode,
                color: orange,
                toolTipText: 'markup.tooltip.pen'
            },
            {
                icon: <IconHighlighter/>,
                onClick: this.activateHighlighter,
                buttonClass: 'highlighter',
                isActive: this.props.isHighlighterMode,
                color: yellow,
                toolTipText: 'markup.tooltip.highlighter'
            },
            {
                icon: <IconImage/>,
                onClick: () => {
                },
                buttonClass: 'insert-image',
                isAttach: true,
                color: audio,
                toolTipText: 'markup.tooltip.image'
            },
            {
                icon: <IconShapes/>,
                onClick: this.activateShapes,
                buttonClass: 'shapes',
                isActive: this.props.isShapeMode,
                color: instructions,
                toolTipText: 'markup.tooltip.shapes'
            },
            {
                icon: <IconStickers/>,
                onClick: this.openStickers,
                buttonClass: 'stickers',
                isActive: this.isStickersMode,
                color: notation,
                toolTipText: 'markup.tooltip.stickers'
            },
        ]
    }


    @action toggleColorPicker = () => {
        this.showColorPicker = !this.showColorPicker
    }

    @action activateShapes = () => {
        this.props.enableShape()
        this.isSubMenu = true
        this.showColorPicker = true;
        this.subMenuType = MarkUpMenuTypes.Shapes
    }

    activateImage = (files) => {
        if (this.props.addCanvasFile) {
            this.props.addCanvasFile(files[0]);
        }
    };

    closeHandle = (e) => {
        e.preventDefault();
        if (this.props.close) {
            this.props.close();
        }
    };

    saveHandle = async (e) => {
        e.preventDefault();
        if (this.props.save) {
            await this.props.save();
        }
    };

    clear = (e) => {
        e.preventDefault();
        if (this.props.clear) {
            this.props.clear();
        }
    };

    @action changeThickness = (thickness) => {
        this.activeThickness = thickness;
        if (this.props.changeThickness) {
            this.props.changeThickness(thickness);
        }
    };


    @action openAnnotationPanel = () => {
        this.isSubMenu = true
        this.subMenuType = MarkUpMenuTypes.Symbols
        if (this.props.enableAnnotation) {
            this.props.enableAnnotation();
        }
    };

    @action activatePencil = () => {
        this.isSubMenu = true;
        this.subMenuType = MarkUpMenuTypes.Pen;
        this.showColorPicker = true;
        if (this.props.enablePencil) {
            this.props.enablePencil();
        }
    };

    @action activateHighlighter = () => {
        this.isSubMenu = true
        this.changeThickness(thicknessMarkupPanel[3])
        this.subMenuType = MarkUpMenuTypes.Highlighter;
        this.showColorPicker = true;
        this.activeColor = defaultHighlighterColor
        this.props.changeColor(defaultHighlighterColor)
        if (this.props.enableHighlighter) {
            this.props.enableHighlighter();
        }
    };

    @action handleColorChange = (color) => {
        this.activeColor = color
        this.props.changeColor(color)
    }

    @action activateText = () => {
        this.isSubMenu = true
        this.subMenuType = MarkUpMenuTypes.Text
        this.showColorPicker = true
        if (this.props.enableText) {
            this.props.enableText();
        }
    };

    activateSelection = () => {
        if (this.props.enableSelection) {
            this.props.enableSelection();
        }
    };

    handleCanvasImage = (iconKey) => {
        const {setCursor, addCanvasImage} = this.props;
        setCursor && setCursor(iconKey);
        addCanvasImage && addCanvasImage(iconKey);
    };

    @action goBackMenu = () => {
        this.isSubMenu = false
        this.isSubMenu = null
        this.showColorPicker = false
        this.activateSelection();
    }

    @action openNotationText = () => {
        this.isSubMenu = true
        this.subMenuType = MarkUpMenuTypes.NotationText
    }

    @action openStickers = () => {
        this.isSubMenu = true;
        this.subMenuType = MarkUpMenuTypes.Stickers;
    }

    render() {
        const {wizardStore, canUndo, canRedo, undo, redo, isSelectionMode, projectStore, addShape, activeShape} = this.props
        const {currentStep} = wizardStore
        const showMarkup = (projectStore.isCanvasEditable && currentStep.type === ProjectAssetsType.Image)
            || (projectStore.isNotationCanvasEditable && currentStep.type === ProjectAssetsType.Notation);
        const disableColor = this.subMenuType === MarkUpMenuTypes.Stickers

        if (!showMarkup) {
            return null
        }

        return (
            <div className="notation-markup-panel">
                <div className="container">
                    <MarkupLeftActions
                        activateSelection={this.activateSelection}
                        isSelectionMode={isSelectionMode}
                        goBackMenu={this.goBackMenu}
                        clear={this.clear}
                        isSubMenu={this.isSubMenu}
                    />
                    <MarkupMainMenu
                        activeColor={this.activeColor}
                        mainButtons={this.buttonsProps}
                        isSubMenu={this.isSubMenu}
                        showPicker={this.showColorPicker}
                        changeColor={this.handleColorChange}
                        activateImage={this.activateImage}
                        activeThickness={this.activeThickness}
                        changeThickness={this.changeThickness}
                        addShape={addShape}
                        type={this.subMenuType}
                        activeShape={activeShape}
                        handleCanvasImage={this.handleCanvasImage}
                    />
                    <MarkupRightActions
                        showActions={this.isSubMenu}
                        canUndo={canUndo}
                        canRedo={canRedo}
                        undo={undo}
                        redo={redo}
                        toggleColorPicker={this.toggleColorPicker}
                        closeHandle={this.closeHandle}
                        activeColor={this.activeColor}
                        disableColor={disableColor}
                    />
                </div>
                <div className='drag-bar'>
                    <SvgIcon className='drag-button' viewBox="0 0 20 8"><DragIcon/></SvgIcon>
                </div>
            </div>
        );
    }
}

export default MarkupPanelContainer;
